import React, { useState, useEffect } from 'react';
import axios from '../../../utils/axios';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import { Checkbox } from '../../../components/ui/Checkbox';
import toast from 'react-hot-toast';

const NotificationRulesTab = () => {
  const [rules, setRules] = useState([]);
  const [stats, setStats] = useState({
    total_rules: 0,
    active_rules: 0,
    inactive_rules: 0,
    total_dispatched: 0
  });
  const [isLoading, setIsLoading] = useState(false);
  const [isEvaluating, setIsEvaluating] = useState(false);
  const [testingRuleId, setTestingRuleId] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingRule, setEditingRule] = useState(null);

  const [newRule, setNewRule] = useState({
    name: "",
    description: "",
    trigger: "warranty_expiring",
    threshold: "30",
    thresholdUnit: "days",
    channels: ["in_app", "email"],
    recipients: ["system_admins", "it_managers"],
    is_active: true,
    priority: "high"
  });

  const triggerOptions = [
    { value: "warranty_expiring", label: "Warranty Expiring", icon: "Clock", description: "Triggered when asset warranty reaches threshold" },
    { value: "audit_deadline", label: "Audit Deadline", icon: "CheckSquare", description: "Triggered before quarterly audit submission deadline" },
    { value: "status_change", label: "Asset Status Change", icon: "RefreshCw", description: "Immediate trigger when status becomes in_repair, lost, or disposed" },
    { value: "location_mismatch", label: "Location Mismatch", icon: "MapPin", description: "Security alert when asset is detected at unapproved site" },
    { value: "bulk_changes", label: "Bulk Asset Changes", icon: "Layers", description: "Triggered when multiple assets are updated in batch" },
    { value: "maintenance_due", label: "Maintenance Due", icon: "Wrench", description: "Reminder for scheduled servicing and preventive care" },
    { value: "license_expiring", label: "License Expiring", icon: "Key", description: "Alert before software license or subscription expires" },
    { value: "asset_overdue", label: "Asset Check-in Overdue", icon: "AlertTriangle", description: "Triggered when loaned hardware is overdue for return" }
  ];

  const thresholdUnitOptions = [
    { value: "immediate", label: "Immediate (0)" },
    { value: "hours", label: "Hours" },
    { value: "days", label: "Days" },
    { value: "weeks", label: "Weeks" },
    { value: "assets", label: "Number of Assets" }
  ];

  const channelOptions = [
    { value: "in_app", label: "In-App Notification", icon: "Bell", color: "text-blue-600 bg-blue-50 border-blue-200 dark:bg-blue-950/40 dark:border-blue-800 dark:text-blue-300" },
    { value: "email", label: "Email Alert", icon: "Mail", color: "text-emerald-600 bg-emerald-50 border-emerald-200 dark:bg-emerald-950/40 dark:border-emerald-800 dark:text-emerald-300" },
    { value: "sms", label: "SMS Message", icon: "Phone", color: "text-amber-600 bg-amber-50 border-amber-200 dark:bg-amber-950/40 dark:border-amber-800 dark:text-amber-300" },
    { value: "webhook", label: "Webhook Integration", icon: "Zap", color: "text-purple-600 bg-purple-50 border-purple-200 dark:bg-purple-950/40 dark:border-purple-800 dark:text-purple-300" }
  ];

  const recipientOptions = [
    { value: "system_admins", label: "System Administrators" },
    { value: "it_managers", label: "IT Managers" },
    { value: "asset_owners", label: "Asset Owners (Assigned Users)" },
    { value: "audit_team", label: "Audit Committee" },
    { value: "it_support", label: "IT Support Team" },
    { value: "security_team", label: "Security & Compliance" },
    { value: "facilities", label: "Facilities Team" },
    { value: "procurement", label: "Procurement Officers" }
  ];

  const priorityOptions = [
    { value: "critical", label: "Critical Priority", color: "text-red-700 bg-red-100 dark:bg-red-950/80 dark:text-red-300 border-red-200 dark:border-red-800" },
    { value: "high", label: "High Priority", color: "text-amber-700 bg-amber-100 dark:bg-amber-950/80 dark:text-amber-300 border-amber-200 dark:border-amber-800" },
    { value: "medium", label: "Medium Priority", color: "text-blue-700 bg-blue-100 dark:bg-blue-950/80 dark:text-blue-300 border-blue-200 dark:border-blue-800" },
    { value: "low", label: "Low Priority", color: "text-slate-700 bg-slate-100 dark:bg-slate-800 dark:text-slate-300 border-slate-200 dark:border-slate-700" }
  ];

  const fetchRules = async () => {
    try {
      setIsLoading(true);
      const response = await axios.get('/notification-rules');
      if (response.data.success) {
        setRules(response.data.data || []);
        if (response.data.stats) {
          setStats(response.data.stats);
        } else {
          const total = (response.data.data || []).length;
          const active = (response.data.data || []).filter(r => r.is_active).length;
          const dispatched = (response.data.data || []).reduce((acc, r) => acc + (r.trigger_count || 0), 0);
          setStats({
            total_rules: total,
            active_rules: active,
            inactive_rules: total - active,
            total_dispatched: dispatched
          });
        }
      }
    } catch (error) {
      console.error('Error fetching notification rules:', error);
      toast.error('Failed to load notification rules.');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchRules();
  }, []);

  const getPriorityBadge = (priority) => {
    const found = priorityOptions.find(p => p.value === priority?.toLowerCase());
    return found || priorityOptions[2];
  };

  const handleAddRule = async () => {
    if (!newRule.name.trim()) {
      toast.error('Please enter a rule name.');
      return;
    }
    if (newRule.channels.length === 0) {
      toast.error('Please select at least one delivery channel (e.g. In-App).');
      return;
    }

    try {
      const response = await axios.post('/notification-rules', {
        name: newRule.name.trim(),
        description: newRule.description,
        trigger: newRule.trigger,
        threshold: newRule.threshold || "0",
        threshold_unit: newRule.thresholdUnit || "days",
        channels: newRule.channels,
        recipients: newRule.recipients,
        is_active: Boolean(newRule.is_active),
        priority: newRule.priority,
      });

      if (response.data.success) {
        toast.success('Notification rule created successfully!');
        fetchRules();
        closeModal();
      }
    } catch (error) {
      console.error(error);
      toast.error(error?.response?.data?.message || 'Failed to create rule');
    }
  };

  const handleEditRule = (rule) => {
    setEditingRule(rule);
    setNewRule({
      name: rule.name || "",
      description: rule.description || "",
      trigger: rule.trigger || "warranty_expiring",
      threshold: rule.threshold || "0",
      thresholdUnit: rule.threshold_unit || rule.thresholdUnit || "days",
      channels: Array.isArray(rule.channels) ? rule.channels : ["in_app"],
      recipients: Array.isArray(rule.recipients) ? rule.recipients : ["system_admins"],
      is_active: Boolean(rule.is_active),
      priority: rule.priority || "medium"
    });
    setShowAddModal(true);
  };

  const handleUpdateRule = async () => {
    if (!newRule.name.trim()) {
      toast.error('Rule name cannot be empty.');
      return;
    }

    try {
      const response = await axios.put(`/notification-rules/${editingRule.id}`, {
        name: newRule.name.trim(),
        description: newRule.description,
        trigger: newRule.trigger,
        threshold: newRule.threshold,
        threshold_unit: newRule.thresholdUnit,
        channels: newRule.channels,
        recipients: newRule.recipients,
        is_active: Boolean(newRule.is_active),
        priority: newRule.priority,
      });

      if (response.data.success) {
        toast.success('Notification rule updated successfully!');
        fetchRules();
        closeModal();
      }
    } catch (error) {
      console.error(error);
      toast.error(error?.response?.data?.message || 'Failed to update rule');
    }
  };

  const handleDeleteRule = async (id) => {
    if (!window.confirm('Are you sure you want to delete this notification rule?')) {
      return;
    }
    try {
      const response = await axios.delete(`/notification-rules/${id}`);
      if (response.data.success) {
        toast.success('Notification rule deleted.');
        fetchRules();
      }
    } catch (error) {
      console.error(error);
      toast.error('Failed to delete rule');
    }
  };

  const handleToggleRule = async (rule) => {
    try {
      const updatedActive = !rule.is_active;
      const response = await axios.put(`/notification-rules/${rule.id}`, {
        ...rule,
        is_active: updatedActive,
      });

      if (response.data.success) {
        toast.success(`Rule "${rule.name}" is now ${updatedActive ? 'Active' : 'Disabled'}.`);
        fetchRules();
      }
    } catch (error) {
      console.error(error);
      toast.error('Failed to toggle rule status');
    }
  };

  // ACTUALLY DISPATCH TEST NOTIFICATION
  const handleTestRule = async (rule) => {
    try {
      setTestingRuleId(rule.id);
      const response = await axios.post(`/notification-rules/test/${rule.id}`);
      if (response.data.success) {
        toast.success(response.data.message || 'Test notification dispatched successfully! Check your notification bell.');
        fetchRules();
      }
    } catch (error) {
      console.error(error);
      toast.error(error?.response?.data?.message || 'Failed to dispatch test notification.');
    } finally {
      setTestingRuleId(null);
    }
  };

  // TRIGGER AUTOMATED RULE EVALUATION
  const handleRunEvaluation = async () => {
    try {
      setIsEvaluating(true);
      const response = await axios.post('/notification-rules/evaluate');
      if (response.data.success) {
        toast.success(response.data.message || 'Notification rules evaluated!');
        fetchRules();
      }
    } catch (err) {
      console.error(err);
      toast.error('Failed to run automated rule evaluation.');
    } finally {
      setIsEvaluating(false);
    }
  };

  const closeModal = () => {
    setShowAddModal(false);
    setEditingRule(null);
    setNewRule({
      name: "",
      description: "",
      trigger: "warranty_expiring",
      threshold: "30",
      thresholdUnit: "days",
      channels: ["in_app", "email"],
      recipients: ["system_admins", "it_managers"],
      is_active: true,
      priority: "high"
    });
  };

  const toggleChannel = (channelValue) => {
    setNewRule(prev => {
      const current = prev.channels || [];
      const updated = current.includes(channelValue)
        ? current.filter(c => c !== channelValue)
        : [...current, channelValue];
      return { ...prev, channels: updated };
    });
  };

  const toggleRecipient = (recipientValue) => {
    setNewRule(prev => {
      const current = prev.recipients || [];
      const updated = current.includes(recipientValue)
        ? current.filter(r => r !== recipientValue)
        : [...current, recipientValue];
      return { ...prev, recipients: updated };
    });
  };

  return (
    <div className="space-y-6">
      {/* Top Banner / Summary Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Total Rules */}
        <div className="bg-card border border-border rounded-xl p-4 shadow-sm flex items-center justify-between">
          <div>
            <span className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Total Rules</span>
            <h4 className="text-2xl font-bold text-foreground mt-1">{stats.total_rules || rules.length}</h4>
            <span className="text-xs text-muted-foreground mt-0.5 block">Configured event triggers</span>
          </div>
          <div className="w-11 h-11 rounded-xl bg-blue-50 text-blue-600 dark:bg-blue-950/60 dark:text-blue-400 flex items-center justify-center shrink-0">
            <Icon name="Bell" size={22} />
          </div>
        </div>

        {/* Active Rules */}
        <div className="bg-card border border-border rounded-xl p-4 shadow-sm flex items-center justify-between">
          <div>
            <span className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Active Rules</span>
            <h4 className="text-2xl font-bold text-emerald-600 dark:text-emerald-400 mt-1">
              {stats.active_rules || rules.filter(r => r.is_active).length}
            </h4>
            <span className="text-xs text-emerald-700/80 dark:text-emerald-400/80 mt-0.5 block">Actively monitoring events</span>
          </div>
          <div className="w-11 h-11 rounded-xl bg-emerald-50 text-emerald-600 dark:bg-emerald-950/60 dark:text-emerald-400 flex items-center justify-center shrink-0">
            <Icon name="CheckCircle" size={22} />
          </div>
        </div>

        {/* Total Dispatched */}
        <div className="bg-card border border-border rounded-xl p-4 shadow-sm flex items-center justify-between">
          <div>
            <span className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Alerts Dispatched</span>
            <h4 className="text-2xl font-bold text-indigo-600 dark:text-indigo-400 mt-1">
              {stats.total_dispatched || rules.reduce((acc, r) => acc + (r.trigger_count || 0), 0)}
            </h4>
            <span className="text-xs text-muted-foreground mt-0.5 block">Delivered via In-App, Email, SMS</span>
          </div>
          <div className="w-11 h-11 rounded-xl bg-indigo-50 text-indigo-600 dark:bg-indigo-950/60 dark:text-indigo-400 flex items-center justify-center shrink-0">
            <Icon name="Send" size={22} />
          </div>
        </div>

        {/* Automated Evaluator Card */}
        <div className="bg-card border border-border rounded-xl p-4 shadow-sm flex flex-col justify-between">
          <div className="flex items-center justify-between">
            <span className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Automated Engine</span>
            <span className="px-2 py-0.5 text-[11px] rounded-full bg-emerald-100 text-emerald-800 dark:bg-emerald-950 dark:text-emerald-300 font-bold flex items-center gap-1">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
              Live
            </span>
          </div>
          <Button
            variant="default"
            size="sm"
            onClick={handleRunEvaluation}
            isLoading={isEvaluating}
            iconName="Play"
            iconPosition="left"
            className="mt-3 w-full text-xs font-semibold"
          >
            Run Rule Evaluation Now
          </Button>
        </div>
      </div>

      {/* Header and Add Action */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 pt-2">
        <div>
          <h3 className="text-xl font-bold text-foreground">Configured Notification Rules</h3>
          <p className="text-sm text-muted-foreground mt-0.5">
            Automated alerts dispatch according to rule triggers, threshold criteria, and target recipient groups.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={fetchRules}
            isLoading={isLoading}
            iconName="RotateCcw"
            iconPosition="left"
          >
            Refresh
          </Button>
          <Button
            variant="default"
            size="sm"
            onClick={() => {
              setEditingRule(null);
              setShowAddModal(true);
            }}
            iconName="Plus"
            iconPosition="left"
          >
            Add Rule
          </Button>
        </div>
      </div>

      {/* Rules List */}
      <div className="space-y-4">
        {isLoading && rules.length === 0 ? (
          <div className="p-12 text-center bg-card rounded-xl border border-border">
            <div className="w-8 h-8 border-3 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-3" />
            <p className="text-sm text-muted-foreground">Loading notification rules...</p>
          </div>
        ) : rules.length > 0 ? (
          rules.map((rule) => {
            const priorityBadge = getPriorityBadge(rule.priority);
            const triggerInfo = triggerOptions.find(t => t.value === rule.trigger);
            const isTesting = testingRuleId === rule.id;

            return (
              <div
                key={rule.id}
                className={`bg-card rounded-xl border transition-all duration-200 p-5 ${
                  rule.is_active ? 'border-border shadow-sm' : 'border-border/60 opacity-80 bg-muted/20'
                }`}
              >
                <div className="flex flex-col lg:flex-row lg:items-start justify-between gap-4">
                  {/* Left: Icon & Core Details */}
                  <div className="flex items-start space-x-3.5 flex-1 min-w-0">
                    <div className={`w-11 h-11 rounded-xl flex items-center justify-center shrink-0 border ${
                      rule.is_active 
                        ? 'bg-primary/10 text-primary border-primary/20' 
                        : 'bg-muted text-muted-foreground border-border'
                    }`}>
                      <Icon name={triggerInfo?.icon || 'Bell'} size={20} />
                    </div>

                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap mb-1.5">
                        <h4 className="font-bold text-base text-card-foreground">{rule.name}</h4>
                        
                        {/* Priority Badge */}
                        <span className={`px-2 py-0.5 rounded-full text-[11px] font-bold border ${priorityBadge.color}`}>
                          {rule.priority ? rule.priority.toUpperCase() : 'MEDIUM'}
                        </span>

                        {/* Status Badge */}
                        <span className={`px-2 py-0.5 rounded-full text-[11px] font-semibold flex items-center gap-1 ${
                          rule.is_active 
                            ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-950 dark:text-emerald-300' 
                            : 'bg-muted text-muted-foreground'
                        }`}>
                          <span className={`w-1.5 h-1.5 rounded-full ${rule.is_active ? 'bg-emerald-500' : 'bg-slate-400'}`} />
                          {rule.is_active ? 'Active' : 'Disabled'}
                        </span>
                      </div>

                      <p className="text-xs text-muted-foreground mb-3 leading-relaxed">
                        {rule.description || 'Automated notification policy rule.'}
                      </p>

                      {/* Rule Specs Grid */}
                      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 p-3 bg-muted/40 rounded-xl border border-border/60 text-xs">
                        <div>
                          <span className="text-[11px] text-muted-foreground block font-medium">Trigger Event:</span>
                          <span className="font-semibold text-foreground flex items-center gap-1.5 mt-0.5">
                            <Icon name={triggerInfo?.icon || 'Zap'} size={13} className="text-primary" />
                            {triggerInfo?.label || rule.trigger}
                          </span>
                        </div>

                        <div>
                          <span className="text-[11px] text-muted-foreground block font-medium">Threshold Condition:</span>
                          <span className="font-semibold text-foreground mt-0.5 block">
                            {rule.threshold && rule.threshold !== '0'
                              ? `${rule.threshold} ${rule.threshold_unit || 'days'}`
                              : 'Immediate on event'}
                          </span>
                        </div>

                        <div>
                          <span className="text-[11px] text-muted-foreground block font-medium">Delivery Channels:</span>
                          <div className="flex flex-wrap gap-1 mt-1">
                            {Array.isArray(rule.channels) && rule.channels.length > 0 ? (
                              rule.channels.map(channel => {
                                const chOpt = channelOptions.find(c => c.value === channel);
                                return (
                                  <span
                                    key={channel}
                                    className={`px-1.5 py-0.5 rounded text-[10px] font-bold border flex items-center gap-1 ${
                                      chOpt?.color || 'bg-muted text-foreground border-border'
                                    }`}
                                  >
                                    <Icon name={chOpt?.icon || 'Bell'} size={10} />
                                    <span>{chOpt?.label || channel}</span>
                                  </span>
                                );
                              })
                            ) : (
                              <span className="text-muted-foreground italic">In-App</span>
                            )}
                          </div>
                        </div>
                      </div>

                      {/* Recipients List */}
                      {Array.isArray(rule.recipients) && rule.recipients.length > 0 && (
                        <div className="mt-2.5 flex items-center gap-1.5 flex-wrap text-xs">
                          <span className="text-muted-foreground text-[11px] font-medium mr-1">Recipients:</span>
                          {rule.recipients.map(rec => {
                            const recOpt = recipientOptions.find(r => r.value === rec);
                            return (
                              <span
                                key={rec}
                                className="px-2 py-0.5 bg-background border border-border/80 rounded-md text-[11px] font-medium text-foreground"
                              >
                                {recOpt?.label || rec}
                              </span>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Right: Actions */}
                  <div className="flex lg:flex-col items-center lg:items-end justify-between gap-2 shrink-0 border-t lg:border-t-0 pt-3 lg:pt-0 border-border">
                    <div className="flex items-center gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleTestRule(rule)}
                        isLoading={isTesting}
                        iconName="Send"
                        iconPosition="left"
                        className="text-xs"
                        title="Dispatch test notification via rule channels"
                      >
                        Test Rule
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleEditRule(rule)}
                        iconName="Edit"
                        title="Edit Rule"
                      />
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDeleteRule(rule.id)}
                        iconName="Trash2"
                        className="text-red-600 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-950/40"
                        title="Delete Rule"
                      />
                    </div>

                    {/* Stats & Quick Toggle */}
                    <div className="flex items-center gap-3 text-xs text-muted-foreground mt-1">
                      <div className="flex items-center gap-1 font-mono text-[11px]">
                        <Icon name="Zap" size={12} className="text-amber-500" />
                        <span>Fired {rule.trigger_count || 0}x</span>
                      </div>
                      <Checkbox
                        checked={rule.is_active}
                        onChange={() => handleToggleRule(rule)}
                        label={rule.is_active ? 'Enabled' : 'Disabled'}
                      />
                    </div>
                  </div>
                </div>
              </div>
            );
          })
        ) : (
          <div className="text-center py-12 bg-card rounded-xl border border-border shadow-sm">
            <Icon name="Bell" size={48} className="mx-auto text-muted-foreground/60 mb-3" />
            <h4 className="font-bold text-foreground">No notification rules configured</h4>
            <p className="text-sm text-muted-foreground mt-1 max-w-md mx-auto">
              Configure notification rules to automatically alert administrators, owners, and IT support when warranties expire or audits are due.
            </p>
            <Button
              variant="default"
              onClick={() => {
                setEditingRule(null);
                setShowAddModal(true);
              }}
              iconName="Plus"
              iconPosition="left"
              className="mt-4"
            >
              Add First Notification Rule
            </Button>
          </div>
        )}
      </div>

      {/* Add / Edit Modal */}
      {showAddModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4 backdrop-blur-sm animate-in fade-in">
          <div className="bg-card rounded-2xl border border-border w-full max-w-2xl max-h-[92vh] flex flex-col shadow-2xl overflow-hidden">
            {/* Modal Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-border">
              <div className="flex items-center gap-2.5">
                <div className="w-8 h-8 rounded-lg bg-primary/10 text-primary flex items-center justify-center">
                  <Icon name={editingRule ? 'Edit' : 'Plus'} size={18} />
                </div>
                <div>
                  <h3 className="font-bold text-base text-foreground">
                    {editingRule ? 'Edit Notification Rule' : 'Create New Notification Rule'}
                  </h3>
                  <p className="text-xs text-muted-foreground">
                    Define trigger events, threshold boundaries, and destination channels.
                  </p>
                </div>
              </div>
              <button
                type="button"
                onClick={closeModal}
                className="p-1 rounded-lg hover:bg-muted text-muted-foreground hover:text-foreground"
              >
                <Icon name="X" size={18} />
              </button>
            </div>

            {/* Modal Body */}
            <div className="p-6 space-y-5 overflow-y-auto flex-1">
              {/* Name & Priority */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <Input
                  label="Rule Name"
                  type="text"
                  value={newRule.name}
                  onChange={(e) => setNewRule(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="e.g. Asset Warranty Expiration Alert"
                  required
                />

                <Select
                  label="Priority Level"
                  options={priorityOptions}
                  value={newRule.priority}
                  onChange={(value) => setNewRule(prev => ({ ...prev, priority: value }))}
                  required
                />
              </div>

              {/* Description */}
              <Input
                label="Rule Description"
                type="text"
                value={newRule.description}
                onChange={(e) => setNewRule(prev => ({ ...prev, description: e.target.value }))}
                placeholder="Explain the purpose of this notification rule"
              />

              {/* Trigger & Threshold */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div className="sm:col-span-1">
                  <Select
                    label="Trigger Event"
                    options={triggerOptions}
                    value={newRule.trigger}
                    onChange={(value) => setNewRule(prev => ({ ...prev, trigger: value }))}
                    required
                  />
                </div>

                <div className="sm:col-span-1">
                  <Input
                    label="Threshold Value"
                    type="number"
                    value={newRule.threshold}
                    onChange={(e) => setNewRule(prev => ({ ...prev, threshold: e.target.value }))}
                    placeholder="30"
                  />
                </div>

                <div className="sm:col-span-1">
                  <Select
                    label="Threshold Unit"
                    options={thresholdUnitOptions}
                    value={newRule.thresholdUnit}
                    onChange={(value) => setNewRule(prev => ({ ...prev, thresholdUnit: value }))}
                  />
                </div>
              </div>

              {/* Delivery Channels Pills */}
              <div>
                <label className="text-xs font-semibold text-foreground block mb-1.5">
                  Notification Delivery Channels *
                </label>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                  {channelOptions.map((ch) => {
                    const isSelected = (newRule.channels || []).includes(ch.value);
                    return (
                      <button
                        key={ch.value}
                        type="button"
                        onClick={() => toggleChannel(ch.value)}
                        className={`p-3 rounded-xl border text-xs font-semibold flex flex-col items-center gap-1.5 transition-all text-center ${
                          isSelected
                            ? 'bg-primary text-primary-foreground border-primary shadow-sm'
                            : 'bg-muted/30 text-muted-foreground border-border hover:bg-muted/70'
                        }`}
                      >
                        <Icon name={ch.icon} size={18} />
                        <span>{ch.label}</span>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Target Recipients Grid */}
              <div>
                <label className="text-xs font-semibold text-foreground block mb-1.5">
                  Target Recipient Groups *
                </label>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 bg-muted/20 p-3 rounded-xl border border-border/60">
                  {recipientOptions.map((rec) => {
                    const isSelected = (newRule.recipients || []).includes(rec.value);
                    return (
                      <label
                        key={rec.value}
                        className="flex items-center space-x-2 text-xs p-1.5 rounded-lg hover:bg-muted/50 cursor-pointer select-none"
                      >
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => toggleRecipient(rec.value)}
                          className="rounded border-border text-primary focus:ring-primary w-4 h-4 cursor-pointer"
                        />
                        <span className={isSelected ? 'font-semibold text-foreground' : 'text-muted-foreground'}>
                          {rec.label}
                        </span>
                      </label>
                    );
                  })}
                </div>
              </div>

              {/* Active Toggle */}
              <div className="pt-1">
                <Checkbox
                  label="Enable Rule Immediately"
                  description="Begin automated event monitoring and notification dispatch right away"
                  checked={newRule.is_active}
                  onChange={(e) => setNewRule(prev => ({ ...prev, is_active: e.target.checked }))}
                />
              </div>
            </div>

            {/* Modal Footer */}
            <div className="flex items-center justify-end space-x-3 px-6 py-4 border-t border-border bg-muted/20">
              <Button variant="outline" onClick={closeModal}>
                Cancel
              </Button>
              <Button
                variant="default"
                onClick={editingRule ? handleUpdateRule : handleAddRule}
                disabled={!newRule.name || !newRule.trigger || (newRule.channels || []).length === 0}
              >
                {editingRule ? 'Update Notification Rule' : 'Create Notification Rule'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default NotificationRulesTab;
